import { useState, useEffect } from 'react'
import { Trash2, Sparkles, Loader2 } from 'lucide-react'
import type { Idea } from '../types'
import { api } from '../services/api'
import { useAppStore } from '../stores/useAppStore'
import Modal from './ui/Modal'
import { CATEGORY_LABELS } from '../lib/labels'
import { generateId } from '../lib/id'

interface IdeaModalProps {
  idea?: Idea | null
  isOpen: boolean
  onClose: () => void
  onSave: (idea: Idea) => void
  onDelete?: (id: string) => void
}

export default function IdeaModal({ idea, isOpen, onClose, onSave, onDelete }: IdeaModalProps) {
  const isEditing = !!idea
  const user = useAppStore((s) => s.user)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('general')
  const [isReady, setIsReady] = useState(false)
  const [errors, setErrors] = useState<{ title?: string; description?: string }>({})
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false)

  useEffect(() => {
    if (idea) {
      setTitle(idea.title)
      setDescription(idea.description)
      setCategory(idea.category)
      setIsReady(idea.status === 'ready')
    } else {
      setTitle('')
      setDescription('')
      setCategory('general')
      setIsReady(false)
    }
    setErrors({})
    setIsGeneratingTitle(false)
  }, [idea, isOpen])

  const generateTitleFromDescription = async (desc: string) => {
    if (!desc.trim() || title.trim()) return // não gera se já tem título
    setIsGeneratingTitle(true)
    try {
      const res = await api.post('/api/v1/agent/generate-title', {
        description: desc.trim(),
      })
      const suggested = typeof res.data?.title === 'string' ? res.data.title.trim() : ''
      // O backend pode responder 200 com uma mensagem de erro no lugar do título
      // (ex.: "OPENAI_API_KEY not set..."). Descarta respostas que claramente
      // são erro em vez de exibi-las cruas no campo.
      const looksLikeError = /api[_ ]?key|not set|error|exception|traceback/i.test(suggested)
      if (suggested && !looksLikeError) {
        setTitle(suggested)
      }
    } catch {
      // fallback silencioso: mantém o título vazio para o usuário preencher
    } finally {
      setIsGeneratingTitle(false)
    }
  }

  const validate = () => {
    const nextErrors: { title?: string; description?: string } = {}
    if (!title.trim()) nextErrors.title = 'Título é obrigatório'
    if (!description.trim()) nextErrors.description = 'Descrição é obrigatória'
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSave = () => {
    if (!validate()) return
    const saved: Idea = {
      id: idea?.id || generateId(),
      title: title.trim(),
      description: description.trim(),
      category,
      status: isReady ? 'ready' : 'open',
      x: idea?.x ?? Math.random() * 400 + 50,
      y: idea?.y ?? Math.random() * 300 + 50,
      created_by: idea?.created_by || user?.name || 'Anônimo',
      created_at: idea?.created_at || new Date().toISOString(),
    }
    onSave(saved)
    onClose()
  }

  const handleDelete = () => {
    if (idea && onDelete) {
      onDelete(idea.id)
      onClose()
    }
  }

  return (
    <Modal open={isOpen} onClose={onClose} title={isEditing ? 'Editar ideia' : 'Nova ideia'} size="sm">
      <div className="space-y-3">
        {/* Descrição primeiro — o título é sugerido pela IA a partir dela */}
        <div>
          <label htmlFor="idea-description" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Descrição *
          </label>
          <textarea
            id="idea-description"
            data-autofocus
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={(e) => generateTitleFromDescription(e.target.value)}
            rows={4}
            className={`w-full px-3 py-2 text-sm rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-agent resize-none ${
              errors.description ? 'ring-2 ring-red-500' : ''
            }`}
            placeholder="Descreva a ideia em detalhes..."
            aria-invalid={!!errors.description}
          />
          {errors.description && (
            <p className="text-[10px] text-red-500 mt-0.5" role="alert">{errors.description}</p>
          )}
          <p className="text-[10px] text-gray-400 mt-0.5">
            {isGeneratingTitle ? (
              <span className="flex items-center gap-1 text-agent">
                <Loader2 size={10} className="animate-spin" aria-hidden="true" />
                Gerando título...
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <Sparkles size={10} aria-hidden="true" />
                Ao sair do campo, o agente sugere um título automaticamente.
              </span>
            )}
          </p>
        </div>

        {/* Título depois */}
        <div>
          <label htmlFor="idea-title" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Título *
          </label>
          <input
            id="idea-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={`w-full px-3 py-2 text-sm rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-agent ${
              errors.title ? 'ring-2 ring-red-500' : ''
            }`}
            placeholder="Nome da ideia"
            aria-invalid={!!errors.title}
          />
          {errors.title && <p className="text-[10px] text-red-500 mt-0.5" role="alert">{errors.title}</p>}
        </div>

        <div>
          <label htmlFor="idea-category" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Categoria
          </label>
          <select
            id="idea-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-agent"
          >
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isReady}
            onChange={(e) => setIsReady(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-agent focus:ring-agent"
          />
          <span className="text-xs text-gray-700 dark:text-gray-300">
            Marcar como pronta para análise
          </span>
        </label>

        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={handleSave}
            className="flex-1 py-2 rounded-lg bg-agent text-white text-sm font-medium hover:bg-agent/90 transition-colors focus:outline-none focus:ring-2 focus:ring-agent"
          >
            {isEditing ? 'Salvar' : 'Adicionar'}
          </button>
          {isEditing && onDelete && (
            <button
              onClick={handleDelete}
              className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
              title="Excluir ideia"
              aria-label="Excluir ideia"
            >
              <Trash2 size={16} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}
